// Pure gating logic for the analytics-chat composer (ChatConsole).
//
// The chat input and sample-query buttons must not let the user
// dispatch a NEW prompt until both:
//   1. the tenant LLM configuration has loaded (we know provider /
//      model / key), and
//   2. the active chat's per-chat agent has finished building.
// Sending before then is exactly what surfaced "Agent not ready" /
// "Chat LLM not configured" — the config was fine, the user was just
// ahead of the async build (O-2081).
//
// Steering an in-flight run reuses the already-built agent, so the
// input stays live while `busy` even though `blocked` is conceptually
// true (you can't START a new prompt, but you can steer the current
// run).
//
// Kept as a pure helper so the branch matrix (loading / !agentReady /
// busy-keeps-live) is unit-testable without a render harness — the
// dashboard has no component-render tests.
//
// @param {object} s
// @param {boolean} s.loading    tenant LLM config still being fetched
// @param {boolean} s.agentReady the active chat's agent has been built
// @param {boolean} s.busy       a run is in flight on the active chat
// @returns {{ blocked: boolean, inputDisabled: boolean }}
//   - blocked: a new prompt cannot be dispatched yet. Drives the
//     sample-query buttons (which can only START a prompt).
//   - inputDisabled: the composer textarea is disabled. Mirrors
//     `blocked` but stays enabled while `busy` so the user can steer.
export function computeComposerGate({ loading, agentReady, busy, harnessTurn = null }) {
    const blocked = Boolean(loading) || !agentReady;
    // While the harness decides on a recipe the composer waits: a prompt sent
    // in that gap took the chat and left the proposal dropped in silence.
    const inputDisabled = (!busy && blocked) || Boolean(harnessTurn);
    return { blocked, inputDisabled };
}

// The composer's placeholder, in the order the states take precedence.
export function composerPlaceholder({ harnessTurn = null, busy = false, loading = false, agentReady = true, steering = false, staged = 0 } = {}) {
    if (harnessTurn) return "Deciding whether to keep a recipe\u2026";
    if (busy) return "Steer the run, or \u2318\u23ce to queue\u2026";
    if (loading) return "Loading your LLM configuration\u2026";
    if (!agentReady) return "Preparing the analytics agent\u2026";
    if (steering) return "Write a steer, one to a line, or press Enter to start\u2026";
    if (staged > 0) return "Caption, or send images alone\u2026";
    return "Ask the analytics graph\u2026";
}
